import { usePerms } from 'contexts/PermsContext';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { useContext, useEffect, useId, useMemo, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { FaChevronDown, FaHouse, FaXmark } from 'react-icons/fa6';
import { PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getLogo } from 'utils/images/image.utils';
import './assets/nav.scss';
import {
  buildSidebarData,
  findOpenDropdownName,
  getEffectiveSelectedPath,
  isDropdownSelected,
} from './navStateUtils';
/** Shows the permitted navigation links and closes the mobile menu after choosing a page. */
export default function MobileNav({ isOpen, open }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationId = useId();
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const authContext = useContext(AuthContext);
  const { isAuthenticated } = authContext;

  const perms = usePerms();

  const selectedPage = getEffectiveSelectedPath(location);
  const pageButtons = useMemo(
    () => (isAuthenticated ? buildSidebarData(perms) : []),
    [isAuthenticated, perms],
  );
  const publicInfoIndex = pageButtons.findIndex((page) => page.name === 'Public Info');
  const primaryPageButtons =
    publicInfoIndex === -1 ? pageButtons : pageButtons.slice(0, publicInfoIndex + 1);
  const secondaryPageButtons = publicInfoIndex === -1 ? [] : pageButtons.slice(publicInfoIndex + 1);

  useEffect(() => {
    if (!isAuthenticated) {
      setOpenDropdownName(null);
      return;
    }

    setOpenDropdownName(findOpenDropdownName(pageButtons, selectedPage));
  }, [isAuthenticated, pageButtons, selectedPage]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
  }, [isAuthenticated]);

  const closeAllDropdowns = () => {
    setOpenDropdownName(null);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdownName((currentDropdownName) =>
      currentDropdownName === dropdownName ? null : dropdownName,
    );
  };

  const scrollMainToTop = () => {
    const mainScroll = document.getElementById('main-scroll-container');
    if (mainScroll?.scrollTo) {
      mainScroll.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navigateOrScroll = (event, href) => {
    // Keep normal link behavior when someone opens a page in another tab.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    event.preventDefault();
    if (window.location.pathname === href) {
      scrollMainToTop();
      isOpen(false);
      return;
    }
    navigate(href);
    isOpen(false);
  };

  const renderDropdown = (dropdown, index) => {
    if (!dropdown.enabled) return null;

    const dropdownSelected = isDropdownSelected(dropdown, selectedPage);
    const dropdownOpen = openDropdownName === dropdown.name;
    const dropdownId = `${navigationId}-submenu-${index}`;

    return (
      <div key={dropdown.name} className="navigation-group">
        <button
          type="button"
          className="navigation-item"
          data-selected={dropdownSelected}
          data-open={dropdownOpen}
          onClick={() => toggleDropdown(dropdown.name)}
          aria-expanded={dropdownOpen}
          aria-controls={dropdownOpen ? dropdownId : undefined}
        >
          <span className="navigation-icon" aria-hidden="true">
            {dropdown.icon()}
          </span>
          <span className="navigation-label">{dropdown.name}</span>
          <FaChevronDown className="navigation-chevron" aria-hidden="true" />
        </button>
        <Collapse in={dropdownOpen} mountOnEnter unmountOnExit>
          <div id={dropdownId} className="navigation-submenu-list">
            {dropdown.options.map((option) => {
              if (!option.enabled) return null;

              return (
                <Link
                  className="navigation-item navigation-submenu-item"
                  key={option.href}
                  to={option.href}
                  aria-current={selectedPage === option.href ? 'page' : undefined}
                  onClick={(event) => navigateOrScroll(event, option.href)}
                >
                  <span className="navigation-icon" aria-hidden="true">
                    {option.icon()}
                  </span>
                  <span className="navigation-label">{option.name}</span>
                </Link>
              );
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  if (isAuthenticated)
    return (
      <nav
        aria-label="Mobile navigation"
        className={`fusion-sidebar-shell navigation-menu navigation-overlay ${open ? 'navigation-open' : ''}`}
      >
        <header className="navigation-brand-area">
          <Link
            to="/dashboard"
            className="navigation-brand-link"
            aria-label="Go to dashboard"
            onClick={() => {
              closeAllDropdowns();
              isOpen(false);
            }}
          >
            <img className="navigation-logo" src={getLogo(true)} alt="Logo" />
          </Link>
          <button
            type="button"
            className="navigation-close"
            aria-label="Close navigation"
            onClick={() => isOpen(false)}
          >
            <FaXmark aria-hidden="true" />
          </button>
        </header>
        <div className="navigation-content customScrollbar-Y">
          {/* Home dashboard */}
          <Link
            className="navigation-item"
            to="/dashboard"
            aria-current={selectedPage === '/dashboard' ? 'page' : undefined}
            onClick={(event) => {
              closeAllDropdowns();
              navigateOrScroll(event, '/dashboard');
            }}
          >
            <span className="navigation-icon" aria-hidden="true">
              <FaHouse />
            </span>
            <span className="navigation-label">Dashboard</span>
          </Link>
          {primaryPageButtons.map((dropdown, index) => renderDropdown(dropdown, index))}

          {/* ExacqVision VMS — the same permission gate as the desktop Sidebar. */}
          {perms.testPermission(
            [8, 9, 10, 11, 12, 13],
            ['ViewExacqVisionOverwatchVMSConnections'],
          ) && (
            <Link
              className="navigation-item"
              to="/vms"
              aria-current={selectedPage === '/vms' ? 'page' : undefined}
              onClick={(event) => {
                closeAllDropdowns();
                navigateOrScroll(event, '/vms');
              }}
            >
              <span className="navigation-icon" aria-hidden="true">
                <PiVideoFill />
              </span>
              <span className="navigation-label">Overwatch VMS</span>
            </Link>
          )}

          {/* Maintenance Dashboard*/}
          {perms.maintenanceLevel > 0 && (
            <Link
              className="navigation-item"
              to="/maintenancedashboard"
              aria-current={selectedPage === '/maintenancedashboard' ? 'page' : undefined}
              onClick={(event) => {
                closeAllDropdowns();
                navigateOrScroll(event, '/maintenancedashboard');
              }}
            >
              <span className="navigation-icon" aria-hidden="true">
                <PiWrenchFill />
              </span>
              <span className="navigation-label">Maintenance Devices</span>
            </Link>
          )}

          {secondaryPageButtons.map((dropdown, index) =>
            renderDropdown(dropdown, primaryPageButtons.length + index),
          )}
        </div>
      </nav>
    );
}
